import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ReverseApi } from "~/functions/api/reverse";
import { useOrganization } from "~/hooks/useOrganization";
import type { KintoneAvailableApp, SelectedApp } from "~/interface/reverse";
import type { RootState } from "~/store";
import FormField from "../../Project/formField";
import { CommonButton } from "../../ui/commonButton";
import CreditLackModal from "../../ui/creditLackModal";
import Modal from "../../ui/modal";
import Stepper, { type StepperStep } from "../../ui/stepper";
import AppSelectionStep from "../AppSelectionStep";
import DependencyAnalysisStep from "../DependencyAnalysisStep";
import DetailedAnalysisSelectionStep from "../DetailedAnalysisSelectionStep";
import ReportCreationStep from "../ReportCreationStep";
import styles from "./index.module.scss";

// 型定義
interface DependencyData {
  connectionOverview?: unknown;
  parsedData?: {
    selectedApps?: unknown[];
    dependencies?: unknown[];
    relationships?: unknown[];
    appInfo?: Record<string, any>;
    statistics?: Record<string, unknown>;
    dependencyGraph?: Record<string, string[]>;
  } | null;
  dependencies?: unknown[];
  relationships?: unknown[];
  appInfo?: Record<string, unknown>;
  statistics?: Record<string, unknown>;
}

interface DetailedAnalysisApp {
  appId: string;
  name: string;
  description?: string;
  iconFileKey?: string | null;
  selected: boolean;
  dependencies: Array<{
    appId: string;
    name: string;
    dependencyType: string;
    fieldCount?: number;
    recordCount?: number;
  }>;
  fieldCount?: number;
  recordCount?: number;
}

interface ReverseEngineeringResultData {
  subdomain: string;
  userId: string;
  password: string;
  selectedApps: string[];
  selectedAppDetails: SelectedApp[];
  systemName: string;
  resultName: string;
  reportId: string | null;
  dfInstanceId: string;
  detailedAnalysisApps: DetailedAnalysisApp[];
}

interface ReverseEngineeringModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: ReverseEngineeringResultData) => void;
}

const steps: StepperStep[] = [
  { id: 1, label: "ログイン" },
  { id: 2, label: "アプリ選択" },
  { id: 3, label: "依存アプリ解析" },
  { id: 4, label: "詳細分析対象選択" },
  { id: 5, label: "詳細分析" },
];

const ReverseEngineeringModal: React.FC<ReverseEngineeringModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const { refresh: refreshOrganization } = useOrganization(currentUser?.organization_id);
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dfInstanceId, setDfInstanceId] = useState<string>("");
  const [reportId, setReportId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    subdomain: "",
    userId: "",
    password: "",
    selectedApps: [] as string[],
    selectedAppDetails: [] as SelectedApp[],
    systemName: "",
  });
  const [dependencyData, setDependencyData] = useState<DependencyData | null>(null);
  const [appsForDetailedAnalysis, setAppsForDetailedAnalysis] = useState<DetailedAnalysisApp[]>([]);
  const [selectedForDetailedAnalysis, setSelectedForDetailedAnalysis] = useState<string[]>([]);
  const [availableApps, setAvailableApps] = useState<KintoneAvailableApp[]>([]);
  const [isResultDispatched, setIsResultDispatched] = useState(false);
  const [finalSelectedApps, setFinalSelectedApps] = useState<DetailedAnalysisApp[]>([]); // 詳細分析対象のアプリ情報を保持
  const [showCreditConfirmModal, setShowCreditConfirmModal] = useState(false);
  const [canFinishDetailAnalysis, setCanFinishDetailAnalysis] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isCreditLackModalOpen, setIsCreditLackModalOpen] = useState(false);
  const [lackCredit, setLackCredit] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setCanFinishDetailAnalysis(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentStep !== 5) {
      setCanFinishDetailAnalysis(false);
    }
  }, [currentStep]);

  // ブラウザ閉じる/リロード時の警告
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // ステップ5（詳細分析中）の場合のみ警告を表示
      if (isOpen && currentStep === 5) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isOpen, currentStep]);

  const handleCreditConfirmation = async () => {
    setShowCreditConfirmModal(false);

    if (!currentProject?.id || !currentUser?.id || !currentUser?.organization_id || !reportId) {
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PROJECT_INFO_MISSING);
      return;
    }

    setIsResultDispatched(false);
    setIsProcessing(true);
    setCanFinishDetailAnalysis(false);

    try {
      const reverseApi = new ReverseApi();

      const selectedAppsWithDetails = selectedForDetailedAnalysis.map((appId) => {
        const app = availableApps.find((a) => a.appId === appId);
        return {
          appId: appId,
          name: app?.name || "",
          description: app?.description || "",
          iconFileKey: app?.iconFileKey || "",
          selected: true,
          dependencies: [],
        };
      });

      const finalSelectedAppIds = selectedForDetailedAnalysis.map((id) => String(id));

      // 詳細分析対象のアプリ情報を保存
      setFinalSelectedApps(selectedAppsWithDetails);

      // startAnalyzeReverseEngineeringをreportIdを使って呼び出す
      const analyzeResult = await reverseApi.startAnalyzeReverseEngineering(
        currentUser.organization_id,
        currentProject.id,
        reportId,
        {
          kintone_apps: selectedAppsWithDetails,
          final_selected_app_ids: finalSelectedAppIds,
        },
      );

      if (
        typeof analyzeResult === "object" &&
        "error" in analyzeResult &&
        analyzeResult.error === "CREDIT_LACK"
      ) {
        // クレジット不足エラー
        setLackCredit(analyzeResult.lackCredit);
        setIsCreditLackModalOpen(true);
      } else if (analyzeResult === true) {
        toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.DETAIL_ANALYSIS_STARTED);
        setCurrentStep(currentStep + 1);
      } else {
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.ANALYSIS_START_FAILED);
        refreshOrganization();
      }
    } catch (error) {
      console.error("Failed to start analysis:", error);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.ANALYSIS_START_ERROR);
      refreshOrganization();
    } finally {
      setIsProcessing(false);
    }
  };

  const createResultData = () => ({
    ...formData,
    resultName: `レポート_${new Date().toLocaleDateString().replace(/\//g, "")}`,
    reportId,
    dfInstanceId,
    detailedAnalysisApps: finalSelectedApps, // 詳細分析対象のアプリ情報を含める
  });

  // モーダルの状態を初期化する関数
  const resetModalState = useCallback(() => {
    setCurrentStep(1);
    setIsProcessing(false);
    setDfInstanceId("");
    setReportId(null);
    setFormData({
      subdomain: "",
      userId: "",
      password: "",
      selectedApps: [],
      selectedAppDetails: [],
      systemName: "",
    });
    setDependencyData(null);
    setAppsForDetailedAnalysis([]);
    setSelectedForDetailedAnalysis([]);
    setAvailableApps([]);
    setIsResultDispatched(false);
    setFinalSelectedApps([]);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // モーダルが閉じられたら状態をリセット
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = async () => {
    // ログインステップから次へ進む場合、認証してアプリ一覧を取得
    if (currentStep === 1) {
      if (!currentProject?.id || !currentUser?.id || !currentUser?.organization_id) {
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PROJECT_INFO_MISSING);
        return;
      }

      setIsProcessing(true);
      try {
        const reverseApi = new ReverseApi();
        const response = await reverseApi.getKintoneAppsList(
          currentUser.organization_id,
          currentProject.id,
          {
            subdomain: formData.subdomain,
            login_id: formData.userId,
            password: formData.password,
          },
        );

        if (response) {
          if (response.instanceId) {
            setDfInstanceId(response.instanceId);
          }
          if (response.apps) {
            setAvailableApps(response.apps);
          }
          toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.AUTH_SUCCESS);
          setCurrentStep(currentStep + 1);
        } else {
          toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.AUTH_FAILED);
        }
      } catch (error) {
        console.error("Failed to authenticate:", error);
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.AUTH_ERROR);
      } finally {
        setIsProcessing(false);
      }
    }
    // アプリ選択から依存関係解析へ進む場合
    else if (currentStep === 2 && formData.selectedApps.length > 0) {
      // prepareReverseEngineeringを呼び出してreportIdとinstanceIdを取得
      if (!currentProject?.id || !currentUser?.id || !currentUser?.organization_id) {
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PROJECT_INFO_MISSING);
        return;
      }

      setIsProcessing(true);
      try {
        const reverseApi = new ReverseApi();

        const selectedAppsWithDetails = formData.selectedApps.map((appId) => {
          const app = availableApps.find((a) => a.appId === appId);
          return {
            appId: appId,
            name: app?.name || "",
            description: app?.description || "",
            iconFileKey: app?.iconFileKey || "",
          };
        });

        // prepareReverseEngineeringを呼び出してreportIdとinstanceIdを取得
        const prepareResult = await reverseApi.prepareReverseEngineering(
          currentUser.organization_id,
          currentProject.id,
          {
            kintone_apps: selectedAppsWithDetails,
            df_instance_id: dfInstanceId, // 認証時に取得したdf_instance_idを使用
          },
        );

        if (prepareResult && "reportId" in prepareResult) {
          setReportId(prepareResult.reportId);
          setDfInstanceId(prepareResult.instanceId); // 新しいinstanceIdで更新
          setCurrentStep(currentStep + 1);
        } else {
          toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PREPARATION_FAILED);
        }
      } catch (error) {
        console.error("Failed to prepare:", error);
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PREPARATION_ERROR);
      } finally {
        setIsProcessing(false);
      }
    }
    // 依存関係解析完了後、詳細分析対象選択へ進む場合
    else if (currentStep === 3) {
      // DependencyAnalysisStepがonCompleteを呼ぶのを待つ
      // onCompleteで自動的にステップ4に遷移する
      return;
    }
    // 詳細分析対象選択から詳細分析へ進む場合
    else if (currentStep === 4) {
      if (selectedForDetailedAnalysis.length > 0) {
        setShowCreditConfirmModal(true);
        return;
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      if (!isResultDispatched) {
        const resultData = createResultData();
        onComplete(resultData);
        setIsResultDispatched(true);
        toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_CREATED);
      }
      // Final step - simply close the modal without showing additional dialogs
      onClose();
    }
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !formData.subdomain || !formData.userId || !formData.password;
      case 2:
        return formData.selectedApps.length === 0;
      case 3:
        return !dependencyData; // 依存関係解析が完了するまで無効
      case 4:
        return selectedForDetailedAnalysis.length === 0;
      default:
        if (currentStep === 5) {
          return !canFinishDetailAnalysis;
        }
        return false;
    }
  };

  const handleAppSelectionChange = useCallback(
    (selectedIds: string[], selectedAppDetails: SelectedApp[]) => {
      setFormData((prev) => ({ ...prev, selectedApps: selectedIds, selectedAppDetails }));
    },
    [],
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        if (isProcessing) {
          return (
            <div className={styles["loadingOverlay"]}>
              <div className={styles["spinner"]} />
              <p className={styles["loadingText"]}>認証中...</p>
            </div>
          );
        }
        return (
          <div className={styles["stepContent"]}>
            <FormField
              label="サブドメイン"
              name="subdomain"
              type="text"
              placeholder="novelworks"
              value={formData.subdomain}
              onChange={handleInputChange}
              required
              infoMessage="kintoneのサブドメインを入力してください"
              infoPosition="right"
              suffix=".cybozu.com"
            />
            <FormField
              label="ログイン名（kintone）"
              name="userId"
              type="text"
              placeholder="ログイン名"
              value={formData.userId}
              onChange={handleInputChange}
              required
              infoMessage="管理者アカウントのログイン名を入力することを推奨します。"
              infoPosition="right"
            />
            <FormField
              label="パスワード（kintone）"
              name="password"
              type="password"
              placeholder="パスワード"
              value={formData.password}
              onChange={handleInputChange}
              required
              infoMessage="ログイン名で指定した管理者アカウントのパスワードを入力してください。"
              infoPosition="right"
            />
          </div>
        );

      case 2:
        return (
          <AppSelectionStep
            selectedApps={formData.selectedApps}
            onSelectionChange={handleAppSelectionChange}
            selectedAppDetails={formData.selectedAppDetails}
            availableApps={availableApps}
          />
        );

      case 3:
        // 依存関係解析ステップ
        return (
          <DependencyAnalysisStep
            selectedApps={formData.selectedAppDetails}
            dfInstanceId={dfInstanceId}
            organizationId={currentUser?.organization_id}
            projectId={currentProject?.id}
            reportId={reportId || ""}
            onComplete={(data) => {
              setDependencyData(data);

              // データを直接使用して次のステップの準備
              const parsedData = data.parsedData || {};
              const appInfo = parsedData.appInfo || {};
              const dependencyGraph = parsedData.dependencyGraph || {};

              // app_info_dataにあるすべてのアプリを処理
              const appsWithDeps = Object.keys(appInfo).map((appId) => {
                const info = appInfo[appId] || {};
                const dependencies = dependencyGraph[appId] || [];

                // 元の選択されたアプリから名前と説明を取得（存在する場合）
                const originalApp = formData.selectedAppDetails.find(
                  (app) => app.appId === appId || app.id === appId,
                );

                // 依存関係のあるアプリの詳細情報を取得
                const dependencyDetails = dependencies.map((depId: string) => ({
                  appId: depId,
                  name: appInfo[depId]?.name || `App ${depId}`,
                  dependencyType: "LOOKUP",
                  fieldCount: appInfo[depId]?.fields
                    ? Object.keys(appInfo[depId].fields).length
                    : 0,
                  recordCount: appInfo[depId]?.recordCount || 0,
                }));

                return {
                  appId: appId,
                  name: originalApp?.name || info.name || `App ${appId}`,
                  description: originalApp?.description || info.description || "",
                  selected: true,
                  fieldCount: info.fields ? Object.keys(info.fields).length : 0,
                  recordCount: info.recordCount || 0,
                  dependencies: dependencyDetails,
                };
              });

              setAppsForDetailedAnalysis(appsWithDeps);

              // 次のステップへ移動
              setCurrentStep(4);
            }}
          />
        );

      case 4:
        // 詳細分析対象選択ステップ
        return (
          <DetailedAnalysisSelectionStep
            appsWithDependencies={appsForDetailedAnalysis}
            onSelectionChange={(selected) => {
              setSelectedForDetailedAnalysis(selected);
            }}
          />
        );

      case 5:
        // 詳細分析ステップ（レポート作成）
        return (
          <ReportCreationStep
            selectedApps={finalSelectedApps.map((app) => ({
              id: app.appId,
              appId: app.appId,
              name: app.name || "",
              description: app.description || "",
            }))}
            dfInstanceId={dfInstanceId}
            organizationId={currentUser?.organization_id}
            projectId={currentProject?.id}
            {...(reportId ? { reportId } : {})}
            onClose={onClose}
            onComplete={() => {}}
            onAllTasksComplete={() => {
              // 全タスク完了時の処理 - 直接遷移
              if (!isResultDispatched) {
                const resultData = createResultData();
                onComplete(resultData);
                setIsResultDispatched(true);
                toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_CREATED);
              }
            }}
            onCompletionAvailabilityChange={(available) => {
              setCanFinishDetailAnalysis(available);
            }}
          />
        );

      default:
        return null;
    }
  };

  const modalContent = (
    <div className={styles["modalBody"]}>
      <div className={styles["stepperContainer"]}>
        <Stepper steps={steps} currentStep={currentStep} />
      </div>

      <div className={styles["contentContainer"]}>{renderStepContent()}</div>

      <div className={styles["modalFooter"]}>
        <div className={styles["footerButtons"]}>
          {currentStep === 1 ? (
            <CommonButton variant="label" onClick={onClose}>
              キャンセル
            </CommonButton>
          ) : (
            <div style={{ width: "100px" }}>{/* 戻るボタンのスペースを確保 */}</div>
          )}
          {currentStep === 2 && (
            <div className={styles["selectedCount"]}>選択中: {formData.selectedApps.length}個</div>
          )}
          {currentStep === steps.length ? (
            canFinishDetailAnalysis ? (
              <CommonButton onClick={handleNext} disabled={isNextDisabled() || isProcessing}>
                {isProcessing ? "処理中..." : "完了"}
              </CommonButton>
            ) : (
              <div style={{ width: "120px" }} />
            )
          ) : (
            <CommonButton onClick={handleNext} disabled={isNextDisabled() || isProcessing}>
              {isProcessing ? "処理中..." : "次へ"}
            </CommonButton>
          )}
        </div>
      </div>
    </div>
  );

  const handleModalClose = () => {
    // ステップ5（詳細分析）の場合は確認ダイアログを表示
    if (currentStep === 5) {
      setShowCloseConfirmation(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <Modal
        label="新規レポート作成"
        isOpen={isOpen}
        onClose={handleModalClose}
        content={modalContent}
        className="reverseEngineeringModal"
      />

      {showCreditConfirmModal && (
        <div
          className={styles["creditModalOverlay"]}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreditConfirmModal(false);
            }
          }}
        >
          <div className={styles["creditModal"]}>
            <div className={styles["creditModalHeader"]}>
              <h3 className={styles["creditModalTitle"]}>詳細分析の実行確認</h3>
            </div>
            <div className={styles["creditModalBody"]}>
              <div className={styles["creditSection"]}>
                <h4>クレジット消費について</h4>
                <p>
                  詳細分析を実行すると、<span className={styles["creditAmount"]}>50クレジット</span>
                  が消費されます。
                </p>
              </div>

              <div className={styles["copyrightWarning"]}>
                <div className={styles["warningHeader"]}>
                  <svg
                    className={styles["warningIcon"]}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.2 21.01 3.55 21H20.45C20.8 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.15C12.67 2.98 12.34 2.89 12 2.89C11.66 2.89 11.33 2.98 11.03 3.15C10.73 3.32 10.47 3.56 10.29 3.86Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>著作権・利用規約について</span>
                </div>
                <p className={styles["warningText"]}>
                  この分析で取得するkintoneアプリ設定やカスタマイズコード等の権利は、お客様またはkintone等のSaaSサービス提供元に帰属します。各サービスの利用規約を確認のうえ、ご自身の判断と責任で実行してください。
                </p>
              </div>

              <p className={styles["confirmMessage"]}>よろしいですか？</p>
            </div>
            <div className={styles["creditModalFooter"]}>
              <button
                className={styles["cancelButton"]}
                onClick={() => {
                  setShowCreditConfirmModal(false);
                }}
              >
                キャンセル
              </button>
              <button
                className={styles["confirmButton"]}
                onClick={() => {
                  handleCreditConfirmation();
                }}
              >
                同意して実行する
              </button>
            </div>
          </div>
        </div>
      )}

      <CreditLackModal
        isOpen={isCreditLackModalOpen}
        onClose={() => setIsCreditLackModalOpen(false)}
        lackCredit={lackCredit}
      />

      {showCloseConfirmation && (
        <div
          className={styles["creditModalOverlay"]}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCloseConfirmation(false);
            }
          }}
        >
          <div className={styles["creditModal"]}>
            <div className={styles["creditModalHeader"]}>
              <h3 className={styles["creditModalTitle"]}>分析を中断しますか？</h3>
            </div>
            <div className={styles["creditModalBody"]}>
              <p className={styles["creditInfo"]}>分析処理はバックグラウンドで継続されますが、</p>
              <p className={styles["confirmMessage"]}>
                このモーダルを閉じると再度この画面を開くことはできません。
              </p>
              <p className={styles["confirmMessage"]}>本当に閉じてもよろしいですか？</p>
            </div>
            <div className={styles["creditModalFooter"]}>
              <button
                className={styles["cancelButton"]}
                onClick={() => {
                  setShowCloseConfirmation(false);
                }}
              >
                キャンセル
              </button>
              <button
                className={styles["confirmButton"]}
                onClick={() => {
                  setShowCloseConfirmation(false);
                  onClose();
                  toast.info(TOAST_MESSAGES.REVERSE_ENGINEERING.BACKGROUND_PROCESSING);
                }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReverseEngineeringModal;
